import { html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ComplexSearchEvents, dropdownWidth, filterWidth, inputWidth } from '../types';
import { WEB_COMPONENT_GZ_CUSTOM_INPUT_SEARCH, WEB_COMPONENT_GZ_SINGLE_SELECT } from '../../../webcomponents.constants';
import { ListItem } from '../../gzSingleSelectList/types';
import { GzSingleSelectWebComponent } from '../../gzSingleSelectDropdown';
import { GzCustomInputSearchWebComponent } from './GzCustomInputSearchWebComponent';
import { InputTypes } from '../../gzInput/types';

/**
 * @summary Type here the description of this component
 * @tag gz-complex-search
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-complex-search')
export class GzComplexSearchWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @property({ type: String, attribute: true, reflect: true })
  dropdownPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  inputPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ attribute: true, reflect: true })
  filterWidth = filterWidth.XXLARGE;

  @property({ attribute: true, reflect: true })
  dropdownWidth = dropdownWidth.MEDIUM;

  @property({ attribute: true, reflect: true })
  inputWidth = inputWidth.LARGE;

  @query(WEB_COMPONENT_GZ_SINGLE_SELECT)
  searchKeyComponent: GzSingleSelectWebComponent;

  @query(WEB_COMPONENT_GZ_CUSTOM_INPUT_SEARCH)
  searchInputComponent: GzCustomInputSearchWebComponent;

  @property({ type: String, attribute: true, reflect: true })
  searchKey: string | number = '';

  @property({ type: String, attribute: true, reflect: true })
  searchValue: string = '';

  @property({ type: Array, attribute: true, reflect: true })
  searchKeyOptions: Array<ListItem> = [];
  /**
   * Default input max length
   */
  @property({ type: Number, attribute: true, reflect: true })
  maxLength = 1000;

  @property({ type: Number, attribute: true, reflect: true })
  minSearchLength = 3;

  @property({ type: Object, attribute: false, reflect: true })
  customFilters: {};

  @state()
  protected originalInputPlaceHolder: string;

  render() {
    return html`
      <div class="d-flex flex-row border">
        <gz-single-select
          @open="${this.handleGzSelectOpen}"
          @close="${this.handleGzSelectClose}"
          labelWidth="${this.dropdownWidth}"
          placeholder="${this.dropdownPlaceholder}"
          searchPlaceholderString="${this.searchPlaceholderString}"
          label="${this.label}"
          .value="${this.searchKey}"
          .filterOptions="${this.searchKeyOptions}"
          @selectionChange="${this.emitComplexSearchChangeEventForDropdown}"
          class="cell-edit-field"
        >
        </gz-single-select>

        <span class="separator"></span>
        <gz-custom-input-search
          @change="${this.onChangeEvent}"
          @search="${this.emitComplexSearchChangeEventForSearch}"
          inputWidth="${this.inputWidth}"
          placeholder="${this.inputPlaceholder}"
          id="input-complex-search"
          searchOnAction
          .value="${this.searchValue}"
          .maxLength="${this.maxLength}"
        ></gz-custom-input-search>
      </div>
    `;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    this.updateComplete.then(() => {
      this.searchKeyComponent.value = this.searchKey;
      this.searchInputComponent.setValue(this.searchValue);
      this.originalInputPlaceHolder = this.inputPlaceholder;
    });

    if (changedProperties.has('filterWidth')) {
      this.style.width = this.returnInputWidth();
      this.style.maxWidth = this.returnInputWidth();
    }
  }

  private returnInputWidth(): string {
    if (this.filterWidth === 'full') {
      return '100%';
    } else {
      return this.filterWidth + 'px';
    }
  }

  private handleGzSelectOpen() {
    this.style.setProperty('--separator-display', 'none');
  }

  private handleGzSelectClose() {
    this.style.setProperty('--separator-display', 'flex');
  }

  protected onChangeEvent(ev) {
    const currentInputValueLength = ev.detail.sourceEvent.length;

    this.searchInputComponent.searchDisabled = this.isInputValueLengthNotAllowed(currentInputValueLength);
  }

  protected isInputValueLengthNotAllowed(inputValueLength) {
    if (this.customFilters.hasOwnProperty(this.searchKeyComponent.value)) {
      return false;
    }

    return inputValueLength > 0 && inputValueLength < this.minSearchLength;
  }

  protected emitComplexSearchChangeEventForDropdown(ev) {

    // Check this, because "searchInputComponent.value", can be changed to empty state later, based on some specific
    // conditions
    let initialInputValueIsEmpty = false;
    if (this.searchInputComponent.value === '') {
      initialInputValueIsEmpty = true;
    }


    const dropdownValue = ev.detail.selectedValue;

    if (this.customFilters.hasOwnProperty(this.searchKeyComponent.value)) {
      this.searchInputComponent.placeholder = this.customFilters[this.searchKeyComponent.value];
      this.searchInputComponent.type = InputTypes.NUMBER;
    } else {
      if (this.originalInputPlaceHolder) {
        this.searchInputComponent.placeholder = this.originalInputPlaceHolder;
        this.searchInputComponent.type = InputTypes.TEXT;
      }
    }

    // Input value can be different from final "value" that is sent to backend. Here we will set
    // the search input icon enabled/disabled based on input value
    const currentValueForSearchInput = this.searchInputComponent.getInputFieldValue();
    this.searchInputComponent.searchDisabled = this.isInputValueLengthNotAllowed(currentValueForSearchInput.length);

    // For custom filters (e.g. process pid), if current value for input OR value send to backend is not a number,
    // then we must clear the value
    if (
      this.customFilters.hasOwnProperty(this.searchKeyComponent.value) &&
      (isNaN(this.searchInputComponent.value as any) || isNaN(currentValueForSearchInput as any))
    ) {
      this.searchInputComponent.clearInputValue(new Event('clear'));
      this.searchInputComponent.searchDisabled = true;
    } else {
      // If form value that is sent to backend is not allowed, clear value in order to not throw error

      if (this.isInputValueLengthNotAllowed(this.searchInputComponent.value.length)) {
        this.searchInputComponent.clearInputValue(new Event('clear'));
        this.searchInputComponent.searchDisabled = true;
      }
    }

    // Do not send event to the parents above, because it will be handled there too. Thus, it will be
    // two events processing, and we do not want that.
    ev.stopPropagation();

    if (initialInputValueIsEmpty) {
      return;
    }

    const event = new CustomEvent(ComplexSearchEvents.COMPLEX_SEARCH_CHANGED, {
      detail: {
        searchKey: dropdownValue,
        searchValue: this.searchInputComponent.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  protected emitComplexSearchChangeEventForSearch(ev) {
    const searchValue = ev.detail.sourceEvent;

    const event = new CustomEvent(ComplexSearchEvents.COMPLEX_SEARCH_CHANGED, {
      detail: {
        searchKey: this.searchKeyComponent.value ?? '',
        searchValue: searchValue,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });
    this.dispatchEvent(event);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-complex-search': GzComplexSearchWebComponent;
  }
}
